import type { Curve, ParsedData } from '../../../../interfaces/toolData';
import type { WaveFileType } from '../../types';
import { cleanTimeSeriesData } from '../../../dataQuality';

/**
 * Parser for JAE Smart DM TELEM files
 * 
 * Differences from MicroPulse TELEM:
 * - Simple key-value format instead of structured CSV
 * - RTC_TIME entries provide timestamps
 * - Different variable names and units
 * - No complex telemetry sequence definitions
 */
export class SmartDmTelemParser {
  static parse(fileContent: string): ParsedData {
    const lines = fileContent.split('\n');
    
    // Validate file format
    if (!lines[0]?.trim().startsWith('SmartDM')) {
      throw new Error('Invalid JAE Smart DM TELEM file format');
    }
    
    if (!lines[1]?.trim().includes('TELEM Log')) {
      throw new Error('Not a JAE Smart DM TELEM file');
    }
    
    // Extract metadata from header
    const metadata = this.extractMetadata(lines);
    
    // Find where the key-value data starts (after header)
    let dataStartIndex = -1;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      // Data starts when we see the first key-value pair (contains a comma but not a colon in metadata format)
      if (line.includes(',') && !line.includes(':') && line !== 'Comments: N/A') {
        dataStartIndex = i;
        break;
      }
    }
    
    if (dataStartIndex === -1) {
      throw new Error('Could not find telemetry data in JAE Smart DM TELEM file');
    }
    
    // Parse key-value data
    const telemData = this.parseTelemData(lines.slice(dataStartIndex));
    
    if (telemData.length === 0) {
      throw new Error('No valid telemetry data found in JAE Smart DM TELEM file');
    }
    
    // Generate curves from time series data
    const curves = this.generateCurves(telemData);
    
    return {
      toolType: 'JAE',
      fileType: 'JAE_SMARTDM_TELEM' as WaveFileType,
      curves,
      metadata,
      dataPreview: {
        totalCount: telemData.length,
        first5: telemData.slice(0, 5)
      }
    };
  }
  
  private static extractMetadata(lines: string[]): Record<string, string> {
    const metadata: Record<string, string> = {};
    
    for (const line of lines) {
      const trimmed = line.trim();
      
      // Extract key-value pairs from header (those with colons but not data)
      if (trimmed.includes(':') && !trimmed.startsWith('RTC_TIME') && !trimmed.includes('GM8A:GM8B')) {
        const [key, ...valueParts] = trimmed.split(':');
        const value = valueParts.join(':').trim();
        
        if (key && value) {
          metadata[key.trim()] = value;
        }
      }
    }
    
    return metadata;
  }
  
  private static parseTelemData(lines: string[]): Array<{timestamp: Date | null, variable: string, value: number | string}> {
    const data: Array<{timestamp: Date | null, variable: string, value: number | string}> = [];
    let currentTimestamp: Date | null = null;
    
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || !trimmed.includes(',')) continue;
      
      const [variable, valueStr] = trimmed.split(',').map(s => s.trim());
      
      if (variable === 'RTC_TIME') {
        // Update current timestamp
        const date = new Date(valueStr);
        if (!isNaN(date.getTime())) {
          currentTimestamp = date;
        }
        continue;
      }
      
      // Parse the value
      let value: number | string = valueStr;
      const numValue = parseFloat(valueStr);
      if (!isNaN(numValue)) {
        value = numValue;
      }
      
      data.push({
        timestamp: currentTimestamp,
        variable,
        value
      });
    }
    
    return data;
  }
  
  private static generateCurves(telemData: Array<{timestamp: Date | null, variable: string, value: number | string}>): Curve[] {
    const curves: Curve[] = [];
    
    // Group data by variable name
    const variableGroups = new Map<string, Array<{timestamp: Date, value: number}>>();
    
    for (const entry of telemData) {
      if (entry.timestamp && typeof entry.value === 'number') {
        if (!variableGroups.has(entry.variable)) {
          variableGroups.set(entry.variable, []);
        }
        variableGroups.get(entry.variable)!.push({
          timestamp: entry.timestamp,
          value: entry.value
        });
      }
    }
    
    // Create curves for each variable
    for (const [variable, dataPoints] of variableGroups) {
      if (dataPoints.length === 0) continue;
      
      // Sort by timestamp
      dataPoints.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      
      const unit = this.getJaeTelemVariableUnit(variable);
      
      const rawCurve: Curve = {
        name: variable,
        xValues: dataPoints.map(dp => dp.timestamp),
        yValues: dataPoints.map(dp => dp.value),
        yUnit: unit
      };
      
      // Clean the data
      const cleanupResult = cleanTimeSeriesData(rawCurve);
      if (cleanupResult.cleanedCurve.xValues.length > 0) {
        curves.push(cleanupResult.cleanedCurve);
      }
    }
    
    return curves;
  }
  
  /**
   * JAE Smart DM specific telemetry variable unit lookup
   * Based on observed variable names in JAE Smart DM TELEM files
   */
  private static getJaeTelemVariableUnit(variable: string): string {
    const unitMap: Record<string, string> = {
      // Orientation measurements
      'TOOLFACE': 'degrees',
      'AZIMUTH': 'degrees',
      'INCLINATION': 'degrees',
      
      // Gamma measurements
      'FOCUSED_GAMMA1': 'cps',
      'FOCUSED_GAMMA2': 'cps',
      'GAMMA_TOTAL': 'cps',
      
      // Temperature
      'TEMPERATURE': 'celsius',
      'TEMP': 'celsius',
      
      // Battery and power
      'BATTERY_VOLTAGE': 'v',
      'BATTERY': 'v',
      'BATV': 'v',
      
      // Rotation and drilling
      'ROTW': 'rpm',
      'RPM': 'rpm',
      'DRPM': 'rpm',
      
      // Vibration and shock (GV series)
      'GV6': 'level',
      'GV6_SSI': 'level', 
      'GV7': 'level',
      'GV7_zVibGLvl': 'g',
      'GV7_xyVibGLvl': 'g',
      'GV7_zShkGLvl': 'g',
      'GV7_xyShkGLvl': 'g',
      
      // Gamma sector measurements (GM8A:GM8B series)
      'GM8A': 'raw',
      'GM8B': 'raw',
    };
    
    // Handle complex variable names
    if (variable.includes('GM8A:GM8B_GC')) {
      return 'cps'; // Gamma count rates
    }
    
    if (variable.includes('GM8A:GM8B_CNF')) {
      return 'config'; // Configuration values
    }
    
    if (variable.includes('_GC')) {
      return 'cps'; // Gamma counts
    }
    
    if (variable.includes('Vib') || variable.includes('VIB')) {
      return 'g'; // Vibration measurements
    }
    
    if (variable.includes('Shk') || variable.includes('SHK')) {
      return 'g'; // Shock measurements
    }
    
    // Default lookup
    const unit = unitMap[variable.toUpperCase()] || unitMap[variable];
    
    if (!unit) {
      console.warn(`Unknown JAE Smart DM telemetry variable: "${variable}". Using default unit.`);
      return 'unknown';
    }
    
    return unit;
  }
} 